// Used in avenue-form.component.html

// Angular
import { KeyValue } from "@angular/common";
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSelectChange } from "@angular/material/select";
import { MatSort } from "@angular/material/sort";

// RXJS/Store
import * as createStore from "#components/content-library/assign-cart/content-builder-store";
import * as LoginStore from "#login/store";
import { select, Store } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Components
import { AvenueAssetDialogComponent } from "#components/content-library/assign-cart/containers/avenue-asset-dialog/avenue-asset-dialog.component";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers";
import { LocalStorage } from "#services/local-storage.service";
import { AssetId } from "@carnegie-learning/common-types";
import { Asset, assetTypeIds, Edition, Perform, PerformMedia } from "@carnegie-learning/cure-types";
import { AssetsService } from "@carnegie-learning/data-access-cure";
import { ASSETS_SERVICE } from "@carnegie-learning/data-access-angular-cure";
import { UpdatedAssetMapService } from "@carnegie-learning/data-access-angular-passport";
import { FeathersServicePagination, FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { PPUser as User } from "cli-types";

@Component({
    selector: "avenue-assets",
    templateUrl: "./avenue-assets.component.html",
    styleUrls: [
        "./avenue-assets.component.scss",
        "../../../../../styles/shared-styles.scss",
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AvenueAssetsComponent implements OnInit, OnChanges, OnDestroy {
    assetsPagination$: Observable<FeathersServicePagination<Asset>>;
    assetsLoaded$?: Observable<boolean>;

    params: FeathersServicePaginationParameters<PerformMedia>;
    initialLimit = 20;

    @Input() initSelectedAssetID?: AssetId;
    @Input() activeProgram?: Edition;
    @Input() selectedEdition?: Edition;
    @Output() onSelectedAsset = new EventEmitter<PerformMedia>();

    selectedAsset$?: Observable<Asset>;
    profileData?: User;
    updatedAssetMap: Map<string, PerformMedia>;
    justAddedPerformMedia: PerformMedia;
    visibilityOpts: KeyValue<string, string>[] = [];
    performTypeOpts: KeyValue<string, string>[] = [];

    private ngUnsubscribe = new Subject();

    constructor(
        private store_create: Store<createStore.CreateState>,
        public dialog: MatDialog,
        @Inject(ASSETS_SERVICE) private assetsService: AssetsService,
        private loginStore: Store<LoginStore.UserState>,
        private localStorage: LocalStorage,
        private updatedAssetMapService: UpdatedAssetMapService,
        private formHelper: FormHelper
    ) {}

    ngOnInit() {
        this.params = this.paramSetup();
        if (this.activeProgram) {
            this.activeProgramSetup(this.activeProgram);
        }

        this.assetsPagination$ = this.getAssets();
        this.updatedAssetMapService.updateMapFromLocalStorage();
        this.updatedAssetMapService.getMap.subscribe((map) => {
            this.updatedAssetMap = map;
            if (this.params) {
                this.assetsPagination$ = this.getAssets();
                if (this.justAddedPerformMedia) {
                    this.updatedAssetMap.forEach((obj) => {
                        if (
                            obj.fileName === this.justAddedPerformMedia.fileName
                        ) {
                            this.onSelectedAsset.emit(
                                this.justAddedPerformMedia
                            );
                        }
                    });
                }
            }
        });

        if (this.initSelectedAssetID) {
            this.selectedAsset$ = this.assetsService.getSelectedAsset(
                this.initSelectedAssetID
            );
        }

        this.assetsLoaded$ = this.store_create
            .pipe(select(createStore.getAssetsLoaded))
            .pipe(takeUntil(this.ngUnsubscribe));

        this.loginStore
            .pipe(select(LoginStore.getUserProfile))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                this.profileData = data;
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["params"] && this.params) {
            this.assetsPagination$ = this.getAssets(); //TODO: Delete? I've never seen this line hit in the debugger
        }
        if (changes.activeProgram.currentValue) {
            this.activeProgram = changes.activeProgram.currentValue;
            this.activeProgramSetup(changes.activeProgram.currentValue);
        }
    }

    onPaginating(increment: number) {
        if (this.params) {
            this.params = {
                ...this.params,
                limit: this.params.limit + increment,
                skip: 0,
            };
            this.assetsPagination$ = this.getAssets();
        }
    }

    onSorting(event: MatSort) {
        if (this.params) {
            this.params = {
                ...this.params,
                skip: 0,
                sorting: { [event.active]: event.direction === 'asc' ? 1 : -1 },
            };
        }
    }

    onSearch(event: string) {
        if (this.params) {
            this.params = {
                ...this.params,
                filter: {
                    ...this.params.filter,
                    displayName: event,
                },
            };
            if (event === "") {
                delete (<any>this.params.filter).displayName; // Have to cast this to any, because it won't let me delete it otherwise.
            }
            this.assetsPagination$ = this.getAssets();
        }
    }

    onFilter(event: MatSelectChange) {
        if (this.params) {
            if (event) {
                this.params = {
                    ...this.params,
                    filter: {
                        ...this.params.filter,
                        visibility: event.value,
                    },
                    skip: 0,
                    limit: this.initialLimit,
                    withDeleted: false,
                };
                if (this.profileData) {
                    this.params.filter.publishedBy = this.profileData.userGUID;
                }
                if (event.value !== "self") {
                    delete this.params?.filter.publishedBy;
                }
            } else {
                delete (<any>this.params.filter).visibility; // Have to cast this to any, because it won't let me delete it otherwise.
            }
            this.assetsPagination$ = this.getAssets();
        }
    }

    onTypeFilter(event: MatSelectChange) {
        if (this.params) {
            if (event && !this.formHelper.bothPerformTypesSelected(event.value)) {
                this.params = {
                    ...this.params,
                    filter: {
                        ...this.params.filter,
                        performType: event.value,
                    },
                    skip: 0,
                    limit: this.initialLimit,
                    withDeleted: false,
                };
            } else {
                delete (<any>this.params.filter).performType; // Have to cast this to any, because it won't let me delete it otherwise.
            }
            this.assetsPagination$ = this.getAssets();
        }
    }

    deleteAsset(asset: Perform) {
        this.store_create.dispatch(createStore.removeAsset({ payload: asset }));
        this.updatedAssetMapService.updateMap(asset.assetId, <any>{
            ...asset,
            isDeleted: true,
        });
        if (this.params) {
            this.assetsPagination$ = this.getAssets();
        }
    }

    // TODO Remove `& { 'audit.createdAt': number }` when we can type check a deep path
    paramSetup(): FeathersServicePaginationParameters<Asset & { 'audit.createdAt': number }> {
        if (!this.params && this.activeProgram) {
            this.visibilityOpts = this.formHelper.getVisibilityOpts();
            this.performTypeOpts = this.formHelper.getPerformTypeOpts();
            if (
                this.selectedEdition &&
                !this.visibilityOpts.some((kv) => kv.key === "global")
            ) {
                this.visibilityOpts.push({
                    key: "global",
                    value: this.selectedEdition.editionName,
                });
            } else {
                this.visibilityOpts.forEach((kv) => {
                    if (kv.key === "global" && this.selectedEdition) {
                        kv.value = this.selectedEdition.editionName;
                    }
                });
            }
            return {
                limit: this.initialLimit,
                skip: 0,
                filter: {
                    assetTypeId: assetTypeIds.PERFORM,
                    isAssignable: false,
                    isResource: false,
                    editionIds: this.activeProgram.editionId ? this.activeProgram.editionId : "",
                    visibility: "global",
                },
                sorting: {"$sort[audit.createdAt]": -1},
            };
        }
        return this.params;
    }

    activeProgramSetup(actProg: Edition) {
        this.params = this.paramSetup();
    }

    openCreateAssetDialog() {
        const dialogRef = this.dialog
            .open(AvenueAssetDialogComponent, {
                data: {
                    subjectId: this.activeProgram?.subjectId,
                    assetAssociation: this.activeProgram,
                },
                disableClose: true,
            })
            .beforeClosed()
            .subscribe((dialogResult) => {
                if (dialogResult && this.params) {
                    this.justAddedPerformMedia = dialogResult;
                    this.assetsPagination$ = this.getAssets();
                }
            });
    }

    openUpdateAssetDialog(asset: Perform) {
        const dialogRef = this.dialog
            .open(AvenueAssetDialogComponent, {
                data: {
                    asset: asset,
                    assetAssociation: this.activeProgram,
                    subjectId: this.activeProgram?.subjectId,
                },
            })
            .beforeClosed()
            .subscribe((dialogResult) => {
                if (dialogResult && this.params) {
                    this.updatedAssetMapService.updateMap(
                        dialogResult.assetId,
                        dialogResult
                    );
                    this.assetsPagination$ = this.getAssets();
                }
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    onSelectAsset($event: PerformMedia) {
        this.onSelectedAsset.emit($event);
    }

    // PAS-12319: Possible better fix?: Instead of always not using cache,
    // add Date cookie to the request to prevent caching.
    // First request will not use cache, then subsequent ones will use cache until
    // the Date cookie is updated on asset update/addition/deletion.
    getAssets() {
        return this.assetsService.withoutCacheQueryParam().getAssets(
            this.params
        );
    }
}
